import { getDOMTheme } from "@marginly/ui/stores/theme";
import {
  CreatePriceLineOptions,
  IPriceLine,
  LineStyle,
  LogicalRange,
  LogicalRangeChangeEventHandler,
  MouseEventParams,
} from "lightweight-charts";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  FRAME_TO_SECONDS,
  PERIOD_TO_FRAME,
  PERIOD_TO_SECONDS,
} from "@/constants/chart";
import { ZERO } from "@/constants/math";
import { MarginlyApiContext } from "@/hooks/api";
import { ICandle } from "@/hooks/candles";
import { usePoolData } from "@/hooks/position";
import type { ChartPeriod } from "@/types/common";
import { toChartCandleSeries } from "@/util/common";
import { getSeconds } from "@/util/core";
import { getRangeFromTree } from "@/util/get-range-from-tree";

import type { CandleChartProps } from "./types";
import { createChartBase, getPriceFormat, getSelectedCandle } from "./util";

const GET_DATA_THRESHOLD = 0.1;

const createCancel = () => {
  const ids: Record<number, boolean> = {};

  const create = () => {
    const id = Math.floor(Math.random() * 2 ** 32);
    ids[id] = false;
    return id;
  };

  const check = (id: number) => Boolean(ids[id]);

  const cancel = (id: number) => {
    ids[id] = true;
  };

  return { create, check, cancel };
};

export const useCandleChart = ({
  viewMode,
  chartPeriod,
  poolAddress,
  openPrice,
  liquidationPrice,
  onCrosshairMove,
  isDownTrend,
  containerRef,
  width,
  uniswapAddress: uniswapPool,
  internalChainId,
}: CandleChartProps) => {
  const { refetch } = usePoolData(poolAddress);
  const frame = PERIOD_TO_FRAME[chartPeriod];
  const offset = PERIOD_TO_SECONDS[chartPeriod];
  const [cancel] = useState(createCancel);
  const [chartBase, setChartBase] =
    useState<ReturnType<typeof createChartBase>>();

  const prevPeriod = useRef<ChartPeriod | null>(null);
  const prevFrom = useRef<number | null>(null);
  const cancelId = useRef<number | null>(null);
  const minKey = useRef<number | null>(null);
  const scrollToEnd = useRef<number | null>(offset);
  const ready = useRef(false);
  const waiting = useRef(false);
  const unsub = useRef<null | (() => void)>();
  const [hasMounted, setHasMounted] = useState(false);

  const key = useMemo(
    () => ({
      chainId: internalChainId,
      poolAddress: uniswapPool,
      frame: PERIOD_TO_FRAME[chartPeriod],
    }),
    [internalChainId, uniswapPool, chartPeriod],
  );

  const { getValues, getTree, watch, waitFor } = useContext(MarginlyApiContext);

  function updatePoints() {
    const now = getSeconds();
    const from = now - offset + FRAME_TO_SECONDS[frame];
    return getRangeFromTree(getTree?.(key), from, now);
  }

  const [points, setPoints] = useState<[number, ICandle][]>(updatePoints);

  if (prevPeriod.current !== chartPeriod && hasMounted) {
    if (cancelId.current) {
      // cancel emit 3
      cancel.cancel(cancelId.current);
    }

    unsub.current?.();
    ready.current = false;
    prevPeriod.current = chartPeriod;
    prevFrom.current = null;
    minKey.current = null;
    setPoints(updatePoints());
    const now = getSeconds();

    const from =
      now - (1 + GET_DATA_THRESHOLD) * offset + FRAME_TO_SECONDS[frame];

    // cancel init 1
    const nextCancel = cancel.create();
    cancelId.current = nextCancel;

    getValues?.(key, from, now)
      .then(() => {
        if (cancel.check(nextCancel)) {
          console.warn("canceled", 1);
          return;
        }

        const tree = getTree!(key);
        const mk = tree?.minKey()!;
        const nextPoints = getRangeFromTree(tree, mk, getSeconds());
        setPoints(nextPoints);
        scrollToEnd.current = offset;
        minKey.current = mk;
        ready.current = true;
        return waitFor?.();
      })
      .then(() => {
        if (cancel.check(nextCancel)) {
          console.warn("canceled", 2);
          return;
        }

        unsub.current = watch?.(key, () => {
          if (cancel.check(nextCancel)) {
            console.warn("canceled", 3);
            return;
          }

          const tree = getTree!(key);
          const mk = tree?.minKey()!; // if there are values there is a key
          minKey.current = mk;
          const nextPoints = getRangeFromTree(tree, mk, getSeconds());
          setPoints(nextPoints);
          refetch();
        });
      })
      .catch((e) => {
        console.error(e);
      });
  }

  const rangeChangeRef = useRef<LogicalRangeChangeEventHandler>();
  const crosshairChangeRef = useRef<(param: MouseEventParams) => void>();
  const theme = getDOMTheme();

  useEffect(() => {
    const textColor = getComputedStyle(
      document.documentElement,
    ).getPropertyValue("--text-primary");

    if (!chartBase?.chart) {
      return;
    }

    chartBase.chart.applyOptions({ layout: { textColor } });
  }, [theme, chartBase]);

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const newChartBase = createChartBase({
      container: containerRef.current,
      width,
    });

    setChartBase(newChartBase);

    return () => {
      newChartBase.unsub();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const prev = useRef<{
    openPrice?: number;
    cOpenPriceLine?: IPriceLine;
    lOpenPriceLine?: IPriceLine;
    liquidationPrice?: number;
    cLiquidationPriceLine?: IPriceLine;
    lLiquidationPriceLine?: IPriceLine;
  }>({});

  useEffect(() => {
    if (!chartBase) {
      return;
    }

    if (openPrice !== prev.current.openPrice) {
      if (prev.current.cOpenPriceLine) {
        chartBase.candleStickSeries.removePriceLine(
          prev.current.cOpenPriceLine,
        );

        prev.current.cOpenPriceLine = undefined;
      }

      if (prev.current.lOpenPriceLine) {
        chartBase.lineSeries.removePriceLine(prev.current.lOpenPriceLine);
        prev.current.lOpenPriceLine = undefined;
      }

      if (!openPrice) {
        prev.current.openPrice = undefined;
      } else {
        const openPriceOpts: CreatePriceLineOptions = {
          price: openPrice,
          // fixme use theme vars
          color: "#457AE6",
          lineWidth: 1,
          lineStyle: LineStyle.Solid,
          axisLabelVisible: true,
        };

        prev.current.cOpenPriceLine =
          chartBase.candleStickSeries.createPriceLine(openPriceOpts);

        prev.current.lOpenPriceLine =
          chartBase.lineSeries.createPriceLine(openPriceOpts);

        prev.current.openPrice = openPrice;
      }
    }

    if (liquidationPrice !== prev.current.liquidationPrice) {
      if (prev.current.cLiquidationPriceLine) {
        chartBase.candleStickSeries.removePriceLine(
          prev.current.cLiquidationPriceLine,
        );

        prev.current.cOpenPriceLine = undefined;
      }

      if (prev.current.lLiquidationPriceLine) {
        chartBase.lineSeries.removePriceLine(
          prev.current.lLiquidationPriceLine,
        );

        prev.current.lOpenPriceLine = undefined;
      }

      if (!liquidationPrice) {
        prev.current.liquidationPrice = undefined;
      } else {
        const liquidationPriceOpts: CreatePriceLineOptions = {
          price: liquidationPrice,
          // fixme use theme vars
          color: "#E54595",
          lineWidth: 1,
          lineStyle: LineStyle.Dashed,
          axisLabelVisible: true,
        };

        prev.current.cLiquidationPriceLine =
          chartBase.candleStickSeries.createPriceLine(liquidationPriceOpts);

        prev.current.lLiquidationPriceLine =
          chartBase.lineSeries.createPriceLine(liquidationPriceOpts);

        prev.current.liquidationPrice = liquidationPrice;
      }
    }
  }, [chartBase, openPrice, liquidationPrice]);

  useEffect(() => {
    const candles = points?.map(toChartCandleSeries);
    const priceFormat = getPriceFormat(candles[0]?.close ?? ZERO);

    if (!chartBase) {
      return;
    }

    chartBase.candleStickSeries.applyOptions({ priceFormat });
    chartBase.lineSeries.applyOptions({ priceFormat });

    chartBase.candleStickSeries.setData(
      candles.map((v) => ({
        ...v,
        open: v.open.toNumber(),
        high: v.high.toNumber(),
        low: v.low.toNumber(),
        close: v.close.toNumber(),
      })),
    );

    chartBase.lineSeries.applyOptions({
      // fixme use theme vars
      color: isDownTrend ? "#E54595" : "#40BF00",
    });

    chartBase.lineSeries.setData(
      candles.map((el) => ({
        time: el.time,
        value: el.close.toNumber(),
      })),
    );

    chartBase.volumeSeries.setData(
      candles.map((candle) => ({
        time: candle.time,
        value: candle.volume,
      })),
    );

    if (scrollToEnd.current) {
      const offset = scrollToEnd.current;
      const period = prevPeriod.current; // should be the same as chartPeriod on happened scroll
      const timescale = chartBase.chart.timeScale();

      if (period) {
        const num = offset / FRAME_TO_SECONDS[PERIOD_TO_FRAME[period]];
        const range = { from: 0, to: num };
        timescale.setVisibleLogicalRange(range);
      }

      timescale.scrollToRealTime();
      scrollToEnd.current = null;
    }
  }, [isDownTrend, chartBase, points]);

  useEffect(() => {
    if (!chartBase) {
      return;
    }

    if (viewMode === "line") {
      chartBase.candleStickSeries.applyOptions({ visible: false });
      chartBase.lineSeries.applyOptions({ visible: true });
      return;
    }

    chartBase.candleStickSeries.applyOptions({ visible: true });
    chartBase.lineSeries.applyOptions({ visible: false });
  }, [viewMode, chartBase]);

  const handleLogicalRangeChange = useCallback(
    (range: LogicalRange | null) => {
      if (!range) {
        return;
      }

      if (!prevFrom.current) {
        prevFrom.current = range.from;
        return;
      }

      const diff = range.from - prevFrom.current;
      const length = range.to - range.from;

      if (
        diff < 0 &&
        range.from < GET_DATA_THRESHOLD * length &&
        minKey.current &&
        !waiting.current
      ) {
        waiting.current = true;
        const to = minKey.current;
        const from = to - offset;

        getValues?.(key, from, to)
          .then(() => {
            const tree = getTree!(key);
            const mk = tree?.minKey()!; // if there are values there is a key
            setPoints(getRangeFromTree(tree, mk, getSeconds()));
            minKey.current = mk;
            waiting.current = false;
          })
          .catch((e) => {
            console.error(e);
            waiting.current = false;
          });
      }

      prevFrom.current = range.from;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key, offset],
  );

  const handleCrosshairMove = useCallback(
    (param: MouseEventParams) => {
      if (!param.time) {
        onCrosshairMove?.(undefined);
        return;
      }

      const time = Number(param.time);
      const tree = getTree!(key);
      const point = tree?.get(time);

      const secFrame = FRAME_TO_SECONDS[key.frame];
      const prevPoint = tree?.get(time - secFrame);

      if (!point || !prevPoint) {
        onCrosshairMove?.(undefined);
        return;
      }

      const selected = getSelectedCandle(
        toChartCandleSeries([point.openTimestamp, point]),
        toChartCandleSeries([prevPoint.openTimestamp, prevPoint]),
      );

      onCrosshairMove?.(selected);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onCrosshairMove, key],
  );

  useEffect(() => {
    if (!chartBase) {
      return;
    }

    const { chart } = chartBase;

    onCrosshairMove &&
      crosshairChangeRef.current &&
      chart.unsubscribeCrosshairMove(crosshairChangeRef.current);

    crosshairChangeRef.current = handleCrosshairMove;

    onCrosshairMove && chart.subscribeCrosshairMove(crosshairChangeRef.current);
  }, [chartBase, handleCrosshairMove, onCrosshairMove]);

  useEffect(() => {
    if (!chartBase) {
      return;
    }

    const { chart } = chartBase;

    rangeChangeRef.current &&
      chart
        .timeScale()
        .unsubscribeVisibleLogicalRangeChange(rangeChangeRef.current);

    rangeChangeRef.current = handleLogicalRangeChange;

    chart
      .timeScale()
      .subscribeVisibleLogicalRangeChange(rangeChangeRef.current);
  }, [chartBase, handleLogicalRangeChange]);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  return chartBase;
};
